import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import { InteriorReelStrip } from "./InteriorReelStrip";
import { SHOWCASE_CUTOFF_DATE } from "../../config/constants";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAY_LABELS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

function CalendarPicker({ showcaseDate, dateOptions, loadShowcase }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Parse showcaseDate into year/month for initial view
  const parseDate = (d) => {
    if (!d) return null;
    const parts = d.split("-");
    return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) - 1 };
  };

  const initial = parseDate(showcaseDate) || (() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  })();

  const [calYear, setCalYear] = useState(initial.year);
  const [calMonth, setCalMonth] = useState(initial.month);

  // Keep calendar in sync when showcaseDate changes externally
  useEffect(() => {
    const p = parseDate(showcaseDate);
    if (p) {
      setCalYear(p.year);
      setCalMonth(p.month);
    }
  }, [showcaseDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dateSet = useMemo(() => new Set(dateOptions || []), [dateOptions]);

  // Build grid cells for current month
  const cells = useMemo(() => {
    const firstDow = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const result = [];
    for (let i = 0; i < firstDow; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const mm = String(calMonth + 1).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      result.push(`${calYear}-${mm}-${dd}`);
    }
    return result;
  }, [calYear, calMonth]);

  const now = new Date();
  const maxYear = now.getFullYear();
  const maxMonth = now.getMonth();

  // Earliest allowed month is September 2025 (cutoff month)
  const cutoffParts = SHOWCASE_CUTOFF_DATE.split("-");
  const minYear = parseInt(cutoffParts[0], 10);
  const minMonth = parseInt(cutoffParts[1], 10) - 1;

  const isPrevDisabled =
    calYear < minYear || (calYear === minYear && calMonth <= minMonth);
  const isNextDisabled =
    calYear > maxYear || (calYear === maxYear && calMonth >= maxMonth);

  const goToPrev = () => {
    if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
    else setCalMonth((m) => m - 1);
  };
  const goToNext = () => {
    if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
    else setCalMonth((m) => m + 1);
  };

  const navBtnStyle = (disabled) => ({
    background: "none",
    border: "none",
    cursor: disabled ? "default" : "pointer",
    fontSize: 18,
    color: disabled ? "#ccc" : "#305CDE",
    padding: "0 8px",
    lineHeight: 1,
    fontFamily: "'Montserrat', sans-serif",
  });

  const displayDate = showcaseDate
    ? (() => {
        const p = parseDate(showcaseDate);
        return `${MONTH_NAMES[p.month].slice(0, 3)} ${String(showcaseDate).slice(8)}, ${p.year}`;
      })()
    : "Select date";

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "13px",
          padding: "6px 12px",
          borderRadius: 10,
          border: "1px solid #ddd",
          background: "white",
          cursor: "pointer",
          color: "#111",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span>📅</span>
        <span>{displayDate}</span>
        <span style={{ fontSize: 10, color: "#777" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            zIndex: 2000,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: 14,
            padding: "12px 14px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
            minWidth: 270,
          }}
        >
          {/* Month navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <button style={navBtnStyle(isPrevDisabled)} disabled={isPrevDisabled} onClick={goToPrev}>
              ‹
            </button>
            <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 14 }}>
              {MONTH_NAMES[calMonth]} {calYear}
            </span>
            <button style={navBtnStyle(isNextDisabled)} disabled={isNextDisabled} onClick={goToNext}>
              ›
            </button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {DAY_LABELS.map((lbl) => (
              <div
                key={lbl}
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  color: "#aaa",
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 600,
                  paddingBottom: 2,
                }}
              >
                {lbl}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
            {cells.map((dateStr, i) => {
              if (!dateStr) return <div key={`empty-${i}`} />;

              const isAvailable = dateSet.has(dateStr) && dateStr >= SHOWCASE_CUTOFF_DATE;
              const isSelected = dateStr === showcaseDate;

              return (
                <button
                  key={dateStr}
                  disabled={!isAvailable}
                  onClick={() => {
                    loadShowcase(dateStr);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: "50%",
                    border: isSelected ? "2px solid #305CDE" : "none",
                    background: isSelected ? "#305CDE" : "transparent",
                    color: isSelected ? "white" : isAvailable ? "#111" : "#ccc",
                    cursor: isAvailable ? "pointer" : "default",
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : isAvailable ? 500 : 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (isAvailable && !isSelected) e.currentTarget.style.background = "#eef1fc";
                  }}
                  onMouseLeave={(e) => {
                    if (isAvailable && !isSelected) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {parseInt(dateStr.slice(8), 10)}
                </button>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
}

export function ShowcaseView({ pair, showcaseDate, dateOptions, loadShowcase, setShowShowcase, showcaseData }) {
  const fastest = showcaseData?.stats?.min_steps;
  const frameSuffix = Number.isFinite(fastest) ? ` ${fastest}-Frame` : "";

  return (
    <div
      className="p-4 max-w-3xl mx-auto"
      style={{
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "14px",
        marginBottom: "12px",
        zIndex: 9999,
        position: "relative",
      }}
    >
      {/* Header: Back button + calendar picker */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginTop: 8,
          marginBottom: 12,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <Button
          onClick={() => setShowShowcase(false)}
          variant="text"
          startIcon={<span style={{ fontSize: 18, lineHeight: 1 }}>←</span>}
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12px",
            borderRadius: "12px",
            px: 1,
            color: "#305CDE",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
          }}
        >
          Back to Today's Reely
        </Button>

        <CalendarPicker
          showcaseDate={showcaseDate}
          dateOptions={dateOptions}
          loadShowcase={loadShowcase}
        />
      </div>

      {/* Movie pair title */}
      {pair && (
        <div
          className="mt-4"
          style={{ textAlign: "center", fontWeight: 700, fontSize: "18px", marginBottom: "8px" }}
        >
          <strong>{pair.start_movie}</strong> → <strong>{pair.end_movie}</strong>
        </div>
      )}

      {!showcaseData ? (
        <div className="mt-6" style={{ textAlign: "center" }}>Loading…</div>
      ) : (
        <>
          {showcaseData.stats ? (
            <div
              style={{
                textAlign: "center",
                fontStyle: "italic",
                fontSize: "14px",
                marginTop: "6px",
                marginBottom: "12px",
              }}
            >
              {`Average: ${showcaseData.stats?.avg_steps ?? "—"} · Fastest: ${showcaseData.stats?.min_steps ?? "—"} · Longest: ${showcaseData.stats?.max_steps ?? "—"}`}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                fontStyle: "italic",
                fontSize: "12px",
                marginTop: "-6px",
                marginBottom: "12px",
              }}
            >
              No data yet for this date
            </div>
          )}

          <section className="mt-6">
            <h3 style={{ fontFamily: "'Montserrat'", fontWeight: 700, fontSize: "16px", marginBottom: "10px" }}>
              {`Most Unique${frameSuffix} Reel`}
            </h3>
            {showcaseData.unique ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <InteriorReelStrip reel={showcaseData.unique} imagesByName={showcaseData?.imagesByName || {}} />
              </div>
            ) : (
              <div className="opacity-60" style={{ fontSize: "14px" }}>No fastest reels yet.</div>
            )}
          </section>

          <section className="mt-6">
            <h3 style={{ fontFamily: "'Montserrat'", fontWeight: 700, fontSize: "16px", marginBottom: "10px" }}>
              {`Most Popular${frameSuffix} Reel`}
            </h3>
            {showcaseData.popular ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <InteriorReelStrip reel={showcaseData.popular} imagesByName={showcaseData?.imagesByName || {}} />
              </div>
            ) : (
              <div className="opacity-60" style={{ fontSize: "14px" }}>No fastest reels yet.</div>
            )}
          </section>

          <hr style={{ marginTop: 16, marginBottom: 10, border: "none", borderTop: "1px solid #eee" }} />

          <SocialLinks />
        </>
      )}
    </div>
  );
}

function SocialLinks() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "10px",
        color: "#111",
        marginTop: "8px",
      }}
    >
      <span style={{ whiteSpace: "nowrap" }}>For older answers, see</span>
      <a href="https://www.reddit.com/r/playreely" target="_blank" rel="noopener noreferrer" aria-label="Reely on Reddit" style={{ display: "inline-flex", alignItems: "center" }}>
        <img src="/reddit_logo.png" alt="Reddit" style={{ width: 18, height: 18, display: "block" }} />
      </a>
      <a href="https://instagram.com/playreely" target="_blank" rel="noopener noreferrer" aria-label="Reely on Instagram" style={{ display: "inline-flex", alignItems: "center" }}>
        <img src="/instagram_icon.png" alt="Instagram" style={{ width: 18, height: 18, display: "block" }} />
      </a>
    </div>
  );
}
